/**
 * Geometry Kingdom, surveyed: the vale, and the inside of all five of its
 * regions (docs/rpg.md §22, `.claude/skills/world-maps/SKILL.md`).
 *
 * **Height is the burden of proof.** This is the first kingdom on the map where
 * knowing a thing stops being enough and you have to show why (its tagline says
 * so), and the ground says it: the measuring is done on the valley floor, where
 * a ruler settles any argument, and the proving is done on the ridge above it,
 * which is reached by one lane and climbed a terrace at a time. Nothing on an
 * upper terrace is reached except through the terrace below — the shape of an
 * argument, drawn as ground.
 *
 * The hamlet, the sanctum and the coast sit on the floor with the vale, because
 * none of them is harder than the others; they are simply different things to
 * be looking at. Only the proof is uphill.
 *
 * **Terrain is scenery and never a lock.** Six maps here are made of walls and
 * cliffs, which is what makes them the shapes they are and also what makes them
 * easy to seal: three cottages in the hamlet had no door cut on the first draft
 * and the sanctum's ring is a circle, the shape that seals hardest.
 * `validateTown` and `validateRegionMap` walk all six on every
 * `npm run content:validate`, and every concept below is an ordinary card on
 * the kingdom page besides.
 */

import type { RegionMap, Town } from '@yuan-math/world';

const KINGDOM = 'kingdom-geometry';

/* =============================================================== the vale === */

/**
 * The kingdom: the vale road along the floor, and one lane up to the ridge.
 *
 * The ridge wall runs the whole north side and there is a single way onto it,
 * which is the point. Everything else — the hamlet under the crag, the sanctum
 * on its green, the shore road east — is off the vale road at the same level.
 */
const GEOMETRY_VALE: Town = {
  kingdomId: KINGDOM,
  caption: 'The vale road along the floor, the hamlet and the sanctum off it, the sea to the east, and one lane climbing to the ridge where things have to be proved.',
  width: 26,
  height: 18,
  /* On the vale road below the notice board, the middle of the kingdom. */
  start: { x: 9, y: 11 },
  rows: [
    '^^^^^^^^^^^^^^^^^^^^^^^^^^',
    'f^^^^^^^^^^^^^^^^^^^^^^^^f',
    'f^^^^^^^^^^^^^^^^^^^^^^^^f',
    'f^^^^^^^^^^^^^^^^^^^^^^^^f',
    'f^^^^^^^^^^^#^^^^^^^^^^^^f',
    'f......######.........~~~f',
    'f.%%%%.#..............~~~f',
    'f.%__%.#.......fff....~~~f',
    'f.%_%%.#.......fff...~~~~f',
    'f......#...........#.~~~~f',
    'f####################~~~~f',
    'f...............#....~~~~f',
    'f...........fff.#####~~~~f',
    'f.."""""""..fff......~~~~f',
    'f.."""""""..fff......~~~~f',
    'f...........fff.......~~~f',
    'f.....................~~~f',
    'ffffffffffffffffffffffffff',
  ],
  places: [
    { kind: 'road', name: 'The Vale Road', emoji: '🗺️', x: 1, y: 10 },
    { kind: 'quests', name: 'The Notice Board', emoji: '📜', x: 9, y: 9 },
    { kind: 'region', regionId: 'region-shape-hamlet', x: 3, y: 9 },
    { kind: 'region', regionId: 'region-measurers-vale', x: 6, y: 13 },
    { kind: 'region', regionId: 'region-proof-ridge', x: 12, y: 4 },
    { kind: 'region', regionId: 'region-circle-sanctum', x: 16, y: 7 },
    { kind: 'region', regionId: 'region-coordinate-coast', x: 20, y: 12 },
    { kind: 'boss', bossId: 'boss-geometry-kingdom', x: 19, y: 9 },
  ],
};

/* =========================================================== the regions === */

/**
 * Measurers' Vale — a working farm, and everything on it is measured.
 *
 * Thirteen concepts, so they are grouped by what is being measured rather than
 * strung along a lane: the clock tower and the weigh house north of the track,
 * the measured field beside them, and the stores south where things are stacked
 * — which is where the volumes are, because a volume is a thing you stack.
 */
const MEASURERS_VALE: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-measurers-vale',
  caption: 'A clock tower, a weigh house, a field marked out in squares, and the stores where things are stacked.',
  width: 20,
  height: 15,
  /* On the farm track between the buildings and the stores. */
  start: { x: 9, y: 8 },
  rows: [
    'ffffffffffffffffffff',
    'f.%%%%..%%%%%......f',
    'f.%__%..%___%..""".f',
    'f.%__%..%___%..""".f',
    'f.%__%..%___%..""".f',
    'f.%_%%..%%_%%..""".f',
    'f..................f',
    'f##################f',
    'f..................f',
    'f."""""".%%%%.%%%%.f',
    'f."""""".%__%.%__%.f',
    'f."""""".%__%.%__%.f',
    'f."""""".%_%%.%_%%.f',
    'f........#.........f',
    'fffffffff#ffffffffff',
  ],
  places: [
    { kind: 'concept', conceptId: 'telling-time', x: 3, y: 2 },
    { kind: 'concept', conceptId: 'elapsed-time', x: 4, y: 4 },
    { kind: 'concept', conceptId: 'mass-and-capacity', x: 9, y: 2 },
    { kind: 'concept', conceptId: 'unit-conversion', x: 11, y: 4 },
    { kind: 'concept', conceptId: 'perimeter', x: 15, y: 2 },
    { kind: 'concept', conceptId: 'area-rectangles', x: 17, y: 4 },
    { kind: 'concept', conceptId: 'area-composite', x: 16, y: 6 },
    { kind: 'concept', conceptId: 'area-triangles', x: 5, y: 6 },
    { kind: 'concept', conceptId: 'volume-unit-cubes', x: 10, y: 10 },
    { kind: 'concept', conceptId: 'volume-composite', x: 11, y: 11 },
    { kind: 'concept', conceptId: 'volume', x: 15, y: 10 },
    { kind: 'concept', conceptId: 'surface-area', x: 16, y: 11 },
    { kind: 'concept', conceptId: 'round-solids', x: 4, y: 10 },
    { kind: 'road', name: 'The Farm Gate', emoji: '📐', x: 9, y: 14 },
  ],
};

/**
 * Shape Hamlet — six cottages, and no two of them the same shape.
 *
 * Three along the top of the green and three below it, each with a different
 * footprint, which is the only thing this region is about. The lane between
 * them runs straight through, so a student can stand in the middle and see that
 * the difference is the buildings and not the ground.
 */
const SHAPE_HAMLET: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-shape-hamlet',
  caption: 'Six cottages round a green, and no two of them the same shape.',
  width: 18,
  height: 12,
  /* On the lane in the middle of the hamlet, cottages either side. */
  start: { x: 8, y: 6 },
  rows: [
    'ffffffffffffffffff',
    'f................f',
    'f.%%%..%%%%..%%%.f',
    'f.%_%..%__%..%_%.f',
    'f.%_%..%_%%..%_%.f',
    'f................f',
    'f################f',
    'f................f',
    'f.%_%%..%_%%.%_%.f',
    'f.%__%..%__%.%_%.f',
    'f.......#........f',
    'ffffffff#fffffffff',
  ],
  places: [
    { kind: 'concept', conceptId: 'shape-attributes', x: 3, y: 3 },
    { kind: 'concept', conceptId: 'equal-shares', x: 8, y: 3 },
    { kind: 'concept', conceptId: 'classify-quadrilaterals', x: 14, y: 3 },
    { kind: 'concept', conceptId: 'symmetry', x: 3, y: 9 },
    { kind: 'concept', conceptId: 'polygons', x: 9, y: 9 },
    { kind: 'concept', conceptId: 'quadrilateral-properties', x: 14, y: 9 },
    { kind: 'road', name: 'The Green Gate', emoji: '📐', x: 8, y: 11 },
  ],
};

/**
 * Proof Ridge — four terraces, and no way onto one except through the last.
 *
 * The angles are on the bottom terrace because they are what everything above
 * is stated in; the reasoning and the constructions are on the second; the
 * triangles on the third; and the results you can only have once the triangles
 * are proved — proportionality, Pythagoras, the special right triangles — are
 * on the top. Two stairs join each terrace to the next, at either end, so the
 * climb is a climb and never a queue.
 */
const PROOF_RIDGE: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-proof-ridge',
  caption: 'Four terraces up the ridge, with the angles at the bottom and nothing reachable except through what is under it.',
  width: 20,
  height: 15,
  /* On the bottom terrace, just inside the gate. */
  start: { x: 9, y: 12 },
  rows: [
    '^^^^^^^^^^^^^^^^^^^^',
    '^..................^',
    '^..................^',
    '^..................^',
    '^..................^',
    '^^^#^^^^^^^^^^^^#^^^',
    '^..................^',
    '^..................^',
    '^^^#^^^^^^^^^^^^#^^^',
    '^..................^',
    '^..................^',
    '^^^#^^^^^^^^^^^^#^^^',
    '^..................^',
    '^..................^',
    '^^^^^^^^^#^^^^^^^^^^',
  ],
  places: [
    { kind: 'concept', conceptId: 'angle-measure', x: 3, y: 13 },
    { kind: 'concept', conceptId: 'angles', x: 8, y: 13 },
    { kind: 'concept', conceptId: 'angle-relationships', x: 13, y: 13 },
    { kind: 'concept', conceptId: 'parallel-lines', x: 17, y: 12 },
    { kind: 'concept', conceptId: 'conditional-statements', x: 3, y: 10 },
    { kind: 'concept', conceptId: 'geometric-proof', x: 9, y: 10 },
    { kind: 'concept', conceptId: 'constructions', x: 15, y: 9 },
    { kind: 'concept', conceptId: 'triangle-congruence', x: 3, y: 7 },
    { kind: 'concept', conceptId: 'triangle-properties', x: 9, y: 7 },
    { kind: 'concept', conceptId: 'similarity', x: 15, y: 6 },
    { kind: 'concept', conceptId: 'triangle-proportionality', x: 5, y: 4 },
    { kind: 'concept', conceptId: 'pythagoras', x: 10, y: 3 },
    { kind: 'concept', conceptId: 'special-triangles', x: 15, y: 4 },
    { kind: 'road', name: 'The Ridge Gate', emoji: '📐', x: 9, y: 14 },
  ],
};

/**
 * Circle Sanctum — a ring of stone with four gaps, and one stone at the centre.
 *
 * The circle itself is the middle; the chords and the arcs stand on the ring
 * where a chord and an arc actually are; and the conics are outside it
 * altogether, because a circle is only the first of them. The four gaps are
 * what stop the ring being a wall — a closed circle would seal every tile
 * outside it off the map, which is the mistake this shape invites.
 */
const CIRCLE_SANCTUM: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-circle-sanctum',
  caption: 'A ring of standing stones with four gaps in it, one stone at the middle, and the cut cones outside the ring altogether.',
  width: 17,
  height: 13,
  /* On the grass south of the ring, with a gap in front of you. */
  start: { x: 8, y: 9 },
  rows: [
    'fffffffffffffffff',
    'f...............f',
    'f......%.%......f',
    'f....%%...%%....f',
    'f...%.......%...f',
    'f...%.......%...f',
    'f...%.......%...f',
    'f....%%...%%....f',
    'f......%.%......f',
    'f...............f',
    'f...............f',
    'f.......#.......f',
    'fffffffffffffffff',
  ],
  places: [
    { kind: 'concept', conceptId: 'circles', x: 8, y: 5 },
    { kind: 'concept', conceptId: 'circle-theorems', x: 8, y: 2 },
    { kind: 'concept', conceptId: 'chords-secants', x: 5, y: 5 },
    { kind: 'concept', conceptId: 'arc-sector', x: 11, y: 5 },
    { kind: 'concept', conceptId: 'conic-sections', x: 2, y: 9 },
    { kind: 'road', name: 'The Sanctum Path', emoji: '📐', x: 8, y: 11 },
  ],
};

/**
 * Coordinate Coast — a shore surveyed into squares.
 *
 * The whole region is a grid of lanes running to the water, which is what a
 * coordinate plane is for: somewhere flat, marked out, where a point can be
 * named rather than pointed at. The plotting is in the first square inland from
 * the corner, and the work that needs two points at once — distance, geometry,
 * the transformations — is further along the same rows.
 */
const COORDINATE_COAST: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-coordinate-coast',
  caption: 'A shore surveyed into squares, where a place can be named instead of pointed at.',
  width: 19,
  height: 13,
  /* In a square inland from the quay, on the survey grid. */
  start: { x: 6, y: 7 },
  rows: [
    'fffffffffffffffffff',
    'f...#...#...#...#.f',
    'f#################f',
    'f...#...#...#...#.f',
    'f...#...#...#...#.f',
    'f#################f',
    'f...#...#...#...#.f',
    'f...#...#...#...#.f',
    'f#################f',
    'f,,,,,,,,,,,,,,,,,f',
    'f~~~~~~~~,~~~~~~~~f',
    'f~~~~~~~~~~~~~~~~~f',
    'f~~~~~~~~~~~~~~~~~f',
  ],
  places: [
    { kind: 'concept', conceptId: 'coordinate-first-quadrant', x: 2, y: 3 },
    { kind: 'concept', conceptId: 'coordinate-plane', x: 6, y: 3 },
    { kind: 'concept', conceptId: 'coordinate-distance', x: 10, y: 3 },
    { kind: 'concept', conceptId: 'coordinate-geometry', x: 14, y: 3 },
    { kind: 'concept', conceptId: 'transformations', x: 6, y: 6 },
    { kind: 'concept', conceptId: 'scale-drawings', x: 10, y: 6 },
    { kind: 'concept', conceptId: 'polar-coordinates', x: 14, y: 6 },
    { kind: 'road', name: 'The Slipway', emoji: '📐', x: 9, y: 10 },
  ],
};

/* -------------------------------------------------------------------------- */

export const GEOMETRY_TOWN: Town = GEOMETRY_VALE;

export const GEOMETRY_REGION_MAPS: readonly RegionMap[] = [
  MEASURERS_VALE,
  SHAPE_HAMLET,
  PROOF_RIDGE,
  CIRCLE_SANCTUM,
  COORDINATE_COAST,
];
